/**
 * Czech catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Czech at a mathematician's register. **Warrant → «Opodstatnění»**
 * (justification — flagged). W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal
 * categories are one / few / many / other (Czech `many` is the decimal/fraction form; the
 * neuter «omezení» is invariant in the nominative plural), so `frameActive` declares exactly
 * those (plus an explicit `=0`). Per INV-STABLE-ID a wrong translation is a presentation
 * concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const CS_CATALOG = {
    locale: 'cs',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Zkoumání',
        [P2.sectionClaims]: 'Tvrzení',
        [P2.sectionIntegrity]: 'Integrita',
        [P2.axisWarrantLabel]: 'Opodstatnění',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirické',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definice} interp {Interpretace} testable {Testovatelné} tested {Testované} refuted {Vyvrácené} program {Výzkumný program} philos {Filosofické} other {Neznámé}}',
        [P2.axisModalLabel]: 'Modální',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Úroveň síly {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Prozkoumatelné',
        [P2.stateRefuted]: 'Vyvráceno',
        [P2.stateOutside]: 'Mimo rozsah',
        [P2.verdictConflict]: 'V konfliktu s {constraints}',
        [P2.verdictOutside]: 'Vyžaduje {missing}',
        [P2.lossRegisterPlain]: 'Zjednodušeno do běžného jazyka — část přesnosti je vynechána.',
        [P2.lossRegisterKid]: 'Zjednodušeno pro mladší čtenáře — mnoho podrobností je vynecháno.',
        [P2.lossProfileDetail]: 'Úplný profil Opodstatnění / Empirické / Modální je zde shrnut.',
        [P2.lossConflictDetail]: 'Podrobnosti konfliktu jsou v tomto zobrazení zkráceny.',
        [P2.laneVerified]: 'Ověřeno',
        [P2.laneUnverified]: 'Neověřeno',
        [P2.laneConflict]: 'Konflikt prokázán',
        [P2.constraintLogic]: 'Logika',
        [P2.constraintLightBends]: 'Světlo se ohýbá v gravitačním poli',
        [P2.constraintParallelPostulate]: 'Postulát o rovnoběžkách',
        [P2.claimScalarGravity]: 'Skalární teorie gravitace',
        [P2.claimNonEuclidean]: 'Neeuklidovská geometrie',
        [P2.claimSubstrateSr]: 'Substrátová interpretace speciální relativity',
        [P2.edgeExtObservation]: 'Vnější pozorování',
        [P2.edgeResourceSample]: 'Sdílený výpočetní zdroj',
        [P2.edgeHeuristicSample]: 'Heuristické vodítko',
        [P2.edgeSharedSample]: 'Společný základ',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Omezení',
        [P2.sectionEdges]: 'Hrany',
        [P2.sectionFrame]: 'Rámec',
        [P2.sectionExposure]: 'Expozice',
        [P2.statusAdoptedFormal]: 'Přijato (formální)',
        [P2.statusAdoptedInterpretive]: 'Přijato (interpretační)',
        [P2.statusSuspended]: 'Pozastaveno',
        [P2.edgeEffectConstraint]: 'Tvoří omezení (může vyvrátit)',
        [P2.edgeEffectResource]: 'Zdroj — bez vlivu na verdikty',
        [P2.edgeEffectHeuristic]: 'Heuristika — bez vlivu na verdikty',
        [P2.edgeEffectShared]: 'Společný základ — bez vlivu na verdikty',
        [P2.frameActive]: '{count, plural, =0 {Žádná aktivní omezení} one {# aktivní omezení} few {# aktivní omezení} many {# aktivního omezení} other {# aktivních omezení}}',
        [P2.exposureNone]: 'Žádné interpretační závislosti',
        [P2.exposureDep]: 'Závisí na {id}',
        [P2.laneTrackMachine]: 'Stroj',
        [P2.laneTrackExpert]: 'Expert',
        [P2.laneTrackCommunity]: 'Komunita',
        [P2.expertAudited]: 'Auditováno',
        [P2.expertInProgress]: 'Probíhá',
        [P2.expertNotReviewed]: 'Nezkontrolováno',
        [P2.communityFlags]: 'Existují označení',
        [P2.communityNone]: 'Žádná označení',
        [P2.controlChallenge]: 'Zpochybnit',
        [P2.controlActivate]: 'Aktivovat',
        [P2.controlMakeInterpretive]: 'Učinit interpretačním',
        [P2.controlImportEdge]: 'Importovat hranu',
        [P2.controlPromote]: 'Povýšit',
        [P2.controlUndo]: 'Zpět',
        [P2.controlRedo]: 'Znovu',
        [P2.controlRegister]: 'Zaregistrovat',
        [P2.promoteRejected]: 'Povýšení zamítnuto',
        [P2.promoteAccepted]: 'Povýšení přijato',
        [P2.promoteReasonRefutationTerminal]: 'Důkaz nemůže napravit vyvrácení.',
        [P2.promoteReasonKeystone]: 'Klíčové povýšení vyžaduje empirickou oporu.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Empirický druh není pevně dán.',
        [P2.promoteReasonModalNotFixed]: 'Modální stupeň není pevně dán.',
        [P2.promoteReasonWarrantNotImproved]: 'Stupeň opodstatnění se nezlepšil.',
        [P2.evidenceComputation]: "{engine} vypočítal {op} = {result} za {config}",
        [P2.lossNumericRounding]: "Výsledek zaokrouhlen na {dp} desetinných míst",
    },
};
//# sourceMappingURL=cs.js.map